import React, { useEffect, useRef, useState, useCallback } from 'react';
import { isPrerendering } from '../utils/prerendering';
import './DesktopDemo.css';

const DesktopDemo = ({ onComplete }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [cursorPosition, setCursorPosition] = useState({ top: '50%', left: '50%' });
  const [isTyping, setIsTyping] = useState(false);
  const [thinkingText, setThinkingText] = useState('Analyzing form...');
  const [currentTarget, setCurrentTarget] = useState(null);
  const [flyout, setFlyout] = useState({ visible: false, top: 0, left: 0, selectedIndex: null });

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const buttonRef = useRef(null);
  const demoRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const cancelledRef = useRef(false);
  const flyoutIdRef = useRef(0);

  // Keep onComplete ref updated
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const typeText = async (text, setter, delay = 100) => {
    for (let i = 0; i <= text.length; i++) {
      if (cancelledRef.current) return;
      setter(text.slice(0, i));
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  };

  const getElementCenterPosition = useCallback((element) => {
    if (!element.current || !demoRef.current) return { top: 0, left: 0 };
    
    try {
      // Get the bounding rects
      const elementRect = element.current.getBoundingClientRect();
      const demoRect = demoRef.current.getBoundingClientRect();
      
      // Calculate the center position relative to the demo container
      const centerLeft = elementRect.left - demoRect.left + (elementRect.width / 2);
      const centerTop = elementRect.top - demoRect.top + (elementRect.height / 2);
      
      // Device and element-specific offset adjustments
      const isMobile = window.innerWidth <= 768;
      const isPasswordField = element.current.type === 'password';
      
      let offsetY;
      if (isMobile) {
        offsetY = 30; // 30px down on mobile
      } else {
        // Desktop: different offsets for different elements
        offsetY = isPasswordField ? 0 : 5; // No offset for password, 5px for others
      }
      
      return { 
        top: centerTop + offsetY, 
        left: centerLeft 
      };
    } catch (error) {
      console.warn('Error calculating element position:', error);
      return { top: 0, left: 0 };
    }
  }, []);

  // Continuous position update for current target
  useEffect(() => {
    if (!currentTarget) return;

    const isMobile = window.innerWidth <= 768;
    let ticking = false;

    const updateCursorPosition = () => {
      const position = getElementCenterPosition(currentTarget);
      setCursorPosition({
        top: `${position.top}px`,
        left: `${position.left}px`
      });
    };

    // Update immediately
    updateCursorPosition();

    // Set up continuous updates - less frequent on mobile to prevent crashes
    const intervalTime = isMobile ? 200 : 50;
    const intervalId = setInterval(updateCursorPosition, intervalTime);

    // Throttled scroll/resize handler
    const handleUpdate = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateCursorPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleUpdate, { passive: true });

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [currentTarget, getElementCenterPosition]);

  const moveCursorTo = (element, duration = 1000) => {
    return new Promise(resolve => {
      // Set the target element for continuous tracking
      setCurrentTarget(element);
      
      setTimeout(() => {
        setCurrentTarget(null); // Stop tracking after movement is complete
        resolve();
      }, duration);
    });
  };



  const showFlyout = useCallback((element, selectIndex = 0) => {
    return new Promise(resolve => {
      if (!element.current || !demoRef.current) {
        resolve();
        return;
      }

      // Increment flyout ID to track this instance
      flyoutIdRef.current += 1;
      const currentFlyoutId = flyoutIdRef.current;

      const position = getElementCenterPosition(element);

      // Show flyout
      setFlyout({
        visible: true,
        top: position.top - 20,
        left: position.left + 30,
        selectedIndex: null
      });

      // After a delay, highlight the selected option
      setTimeout(() => {
        if (cancelledRef.current) return;
        // Only update if this is still the current flyout
        if (flyoutIdRef.current !== currentFlyoutId) return;

        setFlyout(prev => ({ ...prev, selectedIndex: selectIndex }));

        // Resolve early so cursor can start moving while flyout is still visible
        setTimeout(() => {
          if (cancelledRef.current) return;
          resolve();
        }, 400);

        // Hide flyout after a longer delay, but only if this is still the current flyout
        setTimeout(() => {
          if (cancelledRef.current) return;
          // Only hide if no newer flyout has been shown
          if (flyoutIdRef.current !== currentFlyoutId) return;
          setFlyout({ visible: false, top: 0, left: 0, selectedIndex: null });
        }, 1800);
      }, 500);
    });
  }, [getElementCenterPosition]);

  const createClickEffect = useCallback((element) => {
    if (element.current && demoRef.current) {
      element.current.classList.add('ai-clicking');

      // Use the same positioning function for consistency
      const position = getElementCenterPosition(element);

      const effect = document.createElement('div');
      effect.className = 'click-effect';
      effect.style.position = 'absolute';
      effect.style.top = `${position.top}px`;
      effect.style.left = `${position.left}px`;
      effect.style.transform = 'translate(-50%, -50%)';
      effect.style.width = '50px';
      effect.style.height = '50px';
      effect.style.border = '3px solid #8b5cf6';
      effect.style.borderRadius = '50%';
      effect.style.opacity = '0';
      effect.style.pointerEvents = 'none';
      effect.style.animation = 'clickRipple 0.8s ease-out';
      effect.style.boxShadow = '0 0 20px rgba(139, 92, 246, 0.6)';
      effect.style.zIndex = '1001';

      demoRef.current.appendChild(effect);

      setTimeout(() => {
        if (element.current) {
          element.current.classList.remove('ai-clicking');
        }
        if (effect.parentNode) {
          effect.remove();
        }
      }, 800);
    }
  }, [getElementCenterPosition]);

  useEffect(() => {
    // Reset cancellation flag on mount
    cancelledRef.current = false;

    const runAnimation = async () => {
      // Skip during pre-rendering so captured HTML matches React's initial render
      if (isPrerendering) return;

      // Wait for component to mount and render
      await new Promise(resolve => setTimeout(resolve, 500));
      if (cancelledRef.current) return;

      // Move to username field
      setThinkingText('Locating username field...');
      await moveCursorTo(usernameRef, 600);
      if (cancelledRef.current) return;

      setIsTyping(true);
      createClickEffect(usernameRef);
      setThinkingText('Suggesting tests...');

      // Show flyout with "Happy tests" selected
      await showFlyout(usernameRef, 0);
      if (cancelledRef.current) return;

      setThinkingText('Typing username...');

      // Type username
      await new Promise(resolve => setTimeout(resolve, 100));
      if (cancelledRef.current) return;

      await typeText('test.user@example.com', setUsername, 25);
      if (cancelledRef.current) return;

      setIsTyping(false);

      // Move to password field
      setThinkingText('Moving to password field...');
      await moveCursorTo(passwordRef, 500);
      if (cancelledRef.current) return;

      setIsTyping(true);
      createClickEffect(passwordRef);
      setThinkingText('Suggesting tests...');

      // Show flyout with "Sad tests" selected
      await showFlyout(passwordRef, 1);
      if (cancelledRef.current) return;

      setThinkingText('Entering secure password...');

      // Type password (show dots)
      await new Promise(resolve => setTimeout(resolve, 100));
      if (cancelledRef.current) return;

      await typeText('••••••••••••', setPassword, 30);
      if (cancelledRef.current) return;

      setIsTyping(false);

      // Move to button
      setThinkingText('Locating submit button...');
      await moveCursorTo(buttonRef, 500);
      if (cancelledRef.current) return;

      setThinkingText('Suggesting tests...');

      createClickEffect(buttonRef);

      // Show flyout with "Edge tests" selected
      await showFlyout(buttonRef, 2);
      if (cancelledRef.current) return;

      setThinkingText('Form submitted successfully!');

      // Wait and complete
      await new Promise(resolve => setTimeout(resolve, 800));
      if (cancelledRef.current) return;

      if (onCompleteRef.current) onCompleteRef.current();
    };

    runAnimation();

    // Cleanup: cancel animation if component unmounts
    return () => {
      cancelledRef.current = true;
    };
  }, []); // Empty deps - only run once on mount

  const flyoutOptions = [
    {
      label: 'Suggest happy tests',
      color: '#22c55e',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
          <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
        </svg>
      )
    },
    {
      label: 'Suggest sad tests',
      color: '#ef4444',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
          <path d="M12 9v4"/><path d="M12 17h.01"/>
        </svg>
      )
    },
    {
      label: 'Suggest edge tests',
      color: '#f59e0b',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/>
        </svg>
      )
    }
  ];

  return (
    <div className="desktop-app-demo" ref={demoRef}>
      {/* AI Cursor */}
      <div
        className="ai-cursor"
        style={{
          top: cursorPosition.top,
          left: cursorPosition.left,
          transition: currentTarget ?
            'top 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94), left 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94)' :
            'none' // No transition when tracking continuously
        }}
      >
        <div className="cursor-glow"></div>
        <div className="cursor-icon"></div>
        <div className="cursor-trail"></div>
        <div className="ai-thinking">{thinkingText}</div>
      </div>

      {/* Test Suggestion Flyout */}
      {flyout.visible && (
        <div
          className="test-flyout"
          style={{
            top: `${flyout.top}px`,
            left: `${flyout.left}px`
          }}
        >
          <div className="flyout-header">
            <span>Actions</span>
          </div>
          <div className="flyout-options">
            {flyoutOptions.map((option, index) => (
              <div
                key={option.label}
                className={`flyout-option ${flyout.selectedIndex === index ? 'selected' : ''}`}
              >
                <span
                  className="option-icon-box"
                  style={{ background: `${option.color}20`, color: option.color }}
                >
                  {option.icon}
                </span>
                <span className="option-label">{option.label}</span>
                {flyout.selectedIndex === index && <span className="option-check">✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="demo-window">
        <div className="demo-titlebar">
          <div className="demo-title">MyApp - Login Window</div>
          <div className="demo-window-controls">
            <div className="window-control minimize"></div>
            <div className="window-control maximize"></div>
            <div className="window-control close"></div>
          </div>
        </div>
        <div className="demo-content">
          <div className="login-form">
            <h2 className="form-title">Welcome Back</h2>
            <div className="form-group">
              <span className="form-label">User ID</span>
              <div
                ref={usernameRef}
                className={`form-input form-input-fake ${isTyping ? 'ai-typing' : ''}`}
              >
                {username || <span className="input-placeholder">Enter user ID</span>}
              </div>
            </div>
            <div className="form-group">
              <span className="form-label">Secret</span>
              <div
                ref={passwordRef}
                className={`form-input form-input-fake ${isTyping ? 'ai-typing' : ''}`}
              >
                {password || <span className="input-placeholder">Enter secret</span>}
              </div>
            </div>
            <button ref={buttonRef} className="login-button">
              Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopDemo;